import "Pages/GcPrecon/ChangeOrder/styles/changeOrder.scss";
import { useEffect, useState } from "react";
import { get } from "../../../utils/api/apiCaller";
import { ENDPOINTS } from "../../../utils/api/endpoints";
import CoLogTable from "Pages/GcPrecon/ChangeOrder/components/CoLogTable";
import SummaryStrip from "Pages/GcPrecon/ChangeOrder/components/SummaryStrip";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import EmptyState from "Components/Common/EmptyState";
import EmptyChangeOrderIcon from "Assets/Img/EmptyChangeOrderIcon";
import {
  LogRow,
  SummaryItem,
} from "Pages/GcPrecon/ChangeOrder/constants/changeOrderData";
import {
  CO_STATUS_LABELS,
  normalizeChangeOrderStatus,
} from "Pages/GcPrecon/ChangeOrder/constants/changeOrderStatus";

// Lender no longer approves change orders (Owner does) — this is a read-only
// log, same as every other role that can't act on them.

// ── API types ─────────────────────────────────────────────────────────────────

interface RawCOAmount {
  amount?: number;
  currency?: { symbol?: string; code?: string };
}

interface RawCO {
  id: string;
  number?: string;
  coNumber?: string;
  title?: string;
  status?: string;
  amount?: RawCOAmount;
  changeAmount?: RawCOAmount;
  totalAmount?: RawCOAmount;
  extendedDays?: number;
  scheduleImpact?: number | string;
  scheduleImpactDays?: number;
  publishedAt?: string;
  submittedAt?: string;
  createdAt?: string;
}

interface COListResponse {
  data?: RawCO[];
  addOns?: {
    originalContractValue?: RawCOAmount;
    approvedCoSum?: { amount?: RawCOAmount; count?: number };
    pendingCoSum?: { amount?: RawCOAmount; count?: number };
    revisedContractValue?: RawCOAmount;
    revisedContractValueWithPendingCO?: RawCOAmount;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalizeCoStatus(co: RawCO): RawCO {
  const status = normalizeChangeOrderStatus(co.status);
  return status === co.status ? co : { ...co, status };
}

function getSymbol(raw?: RawCOAmount): string {
  return raw?.currency?.symbol ?? "$";
}

function formatMoney(raw?: RawCOAmount): string {
  if (!raw || raw.amount == null) return "—";
  const sym = getSymbol(raw);
  return `${sym}${Math.abs(raw.amount).toLocaleString("en-US")}`;
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatScheduleImpact(val?: number | string): string {
  if (val == null || val === "" || val === 0) return "None";
  if (typeof val === "string") return val;
  return `${val > 0 ? "+" : ""}${val} day${Math.abs(val) !== 1 ? "s" : ""}`;
}

function getAmount(co: RawCO): RawCOAmount | undefined {
  return co.totalAmount ?? co.amount ?? co.changeAmount;
}

function mapToLogRow(co: RawCO): LogRow {
  const amt = getAmount(co);
  const sym = getSymbol(amt);
  const num = amt?.amount;
  const amountStr =
    num != null ? `+${sym}${Math.abs(num).toLocaleString("en-US")}` : "—";
  const s = CO_STATUS_LABELS[co.status ?? ""] ?? {
    label: co.status ?? "—",
    cls: "bPending",
  };
  const submittedDate = co.publishedAt ?? co.submittedAt ?? co.createdAt;
  return {
    id: co.number ?? co.coNumber ?? co.id,
    dbId: co.id,
    description: co.title ?? "—",
    sub: formatDate(submittedDate),
    submitted: formatDate(submittedDate),
    amount: amountStr,
    amountTone: "pos",
    daysImpact: formatScheduleImpact(
      co.extendedDays ?? co.scheduleImpact ?? co.scheduleImpactDays,
    ),
    status: s.label,
    statusClass: s.cls,
  };
}

function buildSummary(addOns?: COListResponse["addOns"]): SummaryItem[] {
  const approvedAmount = addOns?.approvedCoSum?.amount?.amount ?? 0;
  const pendingAmount = addOns?.pendingCoSum?.amount?.amount ?? 0;
  const ifAllApprovedAmount: RawCOAmount = {
    amount: approvedAmount + pendingAmount,
    currency:
      addOns?.approvedCoSum?.amount?.currency ??
      addOns?.pendingCoSum?.amount?.currency,
  };
  return [
    {
      label: "Original Contract",
      value: formatMoney(addOns?.originalContractValue),
    },
    {
      label: "Approved COs",
      value: formatMoney(addOns?.approvedCoSum?.amount),
      tone: "up",
      sub:
        addOns?.approvedCoSum?.count != null
          ? `${addOns.approvedCoSum.count} approved`
          : undefined,
    },
    {
      label: "Pending Approval",
      value: formatMoney(addOns?.pendingCoSum?.amount),
      tone: "blue",
      sub:
        addOns?.pendingCoSum?.count != null
          ? `${addOns.pendingCoSum.count} pending`
          : undefined,
    },
    {
      label: "Revised Contract",
      value: formatMoney(addOns?.revisedContractValue),
      sub: "Approved only",
    },
    {
      label: "If All Approved",
      value: formatMoney(addOns?.revisedContractValueWithPendingCO),
      tone: "up",
      sub: `+${formatMoney(ifAllApprovedAmount)}`,
    },
  ];
}

// ── Component ─────────────────────────────────────────────────────────────────

interface ChangeOrdersSectionProps {
  projectId: string;
}

export default function ChangeOrdersSection({
  projectId,
}: ChangeOrdersSectionProps) {
  const [allCOs, setAllCOs] = useState<RawCO[]>([]);
  const [summaryItems, setSummaryItems] = useState<SummaryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    setError("");

    get(ENDPOINTS.CHANGE_ORDERS.LENDER_LIST(projectId)).then((res) => {
      const { data, error: apiError } = res as {
        data: COListResponse | null;
        error: { message?: string } | null;
      };
      setLoading(false);
      if (apiError) {
        setError(apiError.message ?? "Failed to load change orders.");
        return;
      }
      setAllCOs((data?.data ?? []).map(normalizeCoStatus));
      setSummaryItems(buildSummary(data?.addOns));
    });
  }, [projectId]);

  return (
    <section className="changeOrderPage">
      <h1 className="sectionTitle mb-3">Change Orders</h1>
      <SummaryStrip items={summaryItems} />

      {loading && <TableSkeleton cols={5} rows={3} />}
      {error && <div className="errorState">{error}</div>}

      {!loading &&
        !error &&
        (allCOs.length > 0 ? (
          <CoLogTable
            rows={allCOs.map(mapToLogRow)}
            onOpenRow={() => {}}
            readonly
          />
        ) : (
          <EmptyState
            noBorder={true}
            height="100px"
            icon={<EmptyChangeOrderIcon />}
            title="No Change Orders"
            subtitle="There are no change orders for this project yet."
          />
        ))}
    </section>
  );
}
